import axiosNest from '../api/axiosNest';

/**
 * Podgląd źródła przy prezleceniu (status 28.10.2026 pkt 5; przełącznik
 * `sourcePreview`, S-29.10-01, paczka CS i Administracja). Odpowiada
 * `pre-order/source-preview.ts` w API.
 */
export const FEATURE_SOURCE_PREVIEW = 'sourcePreview';

export interface SourceFile {
  id: number;
  filename: string;
  /** pdf | spreadsheet | document | image | email | other — dane z API. */
  kind: string;
  kindLabel: string;
  size: number | null;
  sizeLabel: string | null;
  /** PDF albo obraz — pokazujemy na miejscu. */
  inline: boolean;
  /** Zdanie dla operatora przy podglądzie albo „Pobierz oryginał”. */
  note: string;
  /**
   * Jak pokazać (przełącznik `preOrderDocuments`, 03.11.2026): pdf | image |
   * svg | tiff | table | none. Brak = jak dotąd (`inline`).
   */
  preview?: 'pdf' | 'image' | 'svg' | 'tiff' | 'heic' | 'table' | 'none';
}

export interface SourceEmail {
  id: number;
  role: string;
  roleLabel: string;
  subject: string | null;
  fromName: string | null;
  fromAddress: string | null;
  toAddresses: string[];
  ccAddresses: string[];
  sourceMailbox: string;
  sentAt: string | null;
  receivedAt: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  attachments: SourceFile[];
}

export interface PreOrderSource {
  preOrderId: number;
  emails: SourceEmail[];
  files: SourceFile[];
  filesEnabled: boolean;
  /** Przełącznik `preOrderDocuments` dla konta — pliki niosą `preview`. */
  documents?: boolean;
}

/** Źródła prezlecenia. Odpowiedź w innym kształcie — puste źródło, bez wywrotki. */
export async function fetchSource(preOrderId: number | string): Promise<PreOrderSource> {
  const { data } = await axiosNest.get<Partial<PreOrderSource> | null>(`/pre-orders/${preOrderId}/source`);
  return {
    preOrderId: Number(preOrderId),
    emails: Array.isArray(data?.emails) ? (data!.emails as SourceEmail[]) : [],
    files: Array.isArray(data?.files) ? (data!.files as SourceFile[]) : [],
    filesEnabled: data?.filesEnabled === true,
    ...(data?.documents === true ? { documents: true } : {}),
  };
}

/** Załącznik maila jako blob (z tokenem w nagłówku, nie w adresie). */
export async function fetchEmailAttachmentBlob(
  preOrderId: number | string,
  emailId: number,
  attachmentId: number,
): Promise<Blob> {
  const { data } = await axiosNest.get<Blob>(
    `/pre-orders/${preOrderId}/source/emails/${emailId}/attachments/${attachmentId}/file`,
    { params: { download: 1 }, responseType: 'blob' },
  );
  return data;
}

/**
 * Plik od klienta w podglądzie źródła — trasa źródła, nie sekcji „Pliki od
 * klienta”: FTL i Trade czytają źródło (29.10.2026), plików nie zmieniają.
 */
export async function fetchClientFileBlob(preOrderId: number | string, fileId: number): Promise<Blob> {
  const { data } = await axiosNest.get<Blob>(`/pre-orders/${preOrderId}/source/files/${fileId}/file`, {
    params: { download: 1 },
    responseType: 'blob',
  });
  return data;
}

/** Plik źródła: załącznik maila albo plik od klienta. */
export function fetchSourceFileBlob(
  preOrderId: number | string,
  origin: { emailId: number } | 'client',
  fileId: number,
): Promise<Blob> {
  return origin === 'client'
    ? fetchClientFileBlob(preOrderId, fileId)
    : fetchEmailAttachmentBlob(preOrderId, origin.emailId, fileId);
}
